import * as z from "zod";

/**
 * Schema for portal analytics events
 * These events represent configuration changes that may affect return rates
 */

// Event category for grouping
export const PortalEventCategory = {
  AI_EXCHANGE_AGENT: "ai_exchange_agent",
  AI_RETURN_REASONS: "ai_return_reasons",
  EXCHANGE_OPTION: "exchange_option",
  EXCHANGE_TYPE: "exchange_type",
  ADJUSTMENT: "adjustment",
} as const;

export type PortalEventCategory =
  (typeof PortalEventCategory)[keyof typeof PortalEventCategory];

// Base fields shared by all events
const BaseEventSchema = z.object({
  id: z.string().describe("Unique identifier for the event"),
  date: z.coerce.date().describe("Date when the event occurred"),
});

// Team-level settings events (from Snowflake snapshots)
const ExchangeAgentEventSchema = BaseEventSchema.extend({
  type: z.literal("exchange_agent"),
  enabled: z
    .boolean()
    .describe(
      "Whether exchange agent was enabled or disabled at the team level",
    ),
});

const VariantRecommendationsEventSchema = BaseEventSchema.extend({
  type: z.literal("variant_recommendations"),
  enabled: z
    .boolean()
    .describe("Whether variant recommendations were enabled or disabled"),
});

const ProductRecommendationsEventSchema = BaseEventSchema.extend({
  type: z.literal("product_recommendations"),
  enabled: z
    .boolean()
    .describe("Whether product recommendations were enabled or disabled"),
});

const ProductsAsVariantsEventSchema = BaseEventSchema.extend({
  type: z.literal("products_as_variants"),
  enabled: z
    .boolean()
    .describe("Whether products as variants was enabled or disabled"),
});

// Flow-level events (from Flow collection)
const FlowEventBaseSchema = BaseEventSchema.extend({
  flowId: z.string().describe("Flow ID for navigation"),
  stepIndex: z.number().describe("Step index in the flow"),
  stepName: z.string().optional().describe("Human-readable name of the step"),
});

const FlowExchangeAgentEventSchema = FlowEventBaseSchema.extend({
  type: z.literal("flow_exchange_agent"),
  enabled: z
    .boolean()
    .describe("Whether exchange agent was enabled or disabled for this step"),
});

const AiReturnReasonsEventSchema = FlowEventBaseSchema.extend({
  type: z.literal("ai_return_reasons"),
  enabled: z
    .boolean()
    .describe("Whether AI return reasons step was added or removed"),
});

const ExchangeOptionEventSchema = FlowEventBaseSchema.extend({
  type: z.literal("exchange_option"),
  enabled: z
    .boolean()
    .describe("Whether exchange option was enabled or disabled for this step"),
});

const ExchangeTypeEventSchema = FlowEventBaseSchema.extend({
  type: z.literal("exchange_type"),
  exchangeType: z
    .enum(["sameItem", "variant", "exchangeGroups", "advanced"])
    .describe("The type of exchange that changed"),
  enabled: z
    .boolean()
    .describe("Whether this exchange type was enabled or disabled"),
});

const AdjustmentEventSchema = FlowEventBaseSchema.extend({
  type: z.literal("adjustment"),
  adjustmentType: z
    .enum(["exchange", "storeCredit", "refund"])
    .describe("The type of adjustment that changed"),
  previousValue: z.string().optional().describe("Previous adjustment value"),
  newValue: z.string().describe("New adjustment value"),
});

// Discriminated union of all event types
export const PortalEventSchema = z.discriminatedUnion("type", [
  ExchangeAgentEventSchema,
  VariantRecommendationsEventSchema,
  ProductRecommendationsEventSchema,
  ProductsAsVariantsEventSchema,
  FlowExchangeAgentEventSchema,
  AiReturnReasonsEventSchema,
  ExchangeOptionEventSchema,
  ExchangeTypeEventSchema,
  AdjustmentEventSchema,
]);

export type PortalEvent = z.infer<typeof PortalEventSchema>;

// Individual event types for convenience
export type ExchangeAgentEvent = z.infer<typeof ExchangeAgentEventSchema>;
export type VariantRecommendationsEvent = z.infer<
  typeof VariantRecommendationsEventSchema
>;
export type ProductRecommendationsEvent = z.infer<
  typeof ProductRecommendationsEventSchema
>;
export type ProductsAsVariantsEvent = z.infer<
  typeof ProductsAsVariantsEventSchema
>;
export type FlowExchangeAgentEvent = z.infer<
  typeof FlowExchangeAgentEventSchema
>;
export type AiReturnReasonsEvent = z.infer<typeof AiReturnReasonsEventSchema>;
export type ExchangeOptionEvent = z.infer<typeof ExchangeOptionEventSchema>;
export type ExchangeTypeEvent = z.infer<typeof ExchangeTypeEventSchema>;
export type AdjustmentEvent = z.infer<typeof AdjustmentEventSchema>;

// Get category for an event
export function getEventCategory(event: PortalEvent): PortalEventCategory {
  switch (event.type) {
    case "exchange_agent":
    case "variant_recommendations":
    case "product_recommendations":
    case "products_as_variants":
    case "flow_exchange_agent":
      return PortalEventCategory.AI_EXCHANGE_AGENT;
    case "ai_return_reasons":
      return PortalEventCategory.AI_RETURN_REASONS;
    case "exchange_option":
      return PortalEventCategory.EXCHANGE_OPTION;
    case "exchange_type":
      return PortalEventCategory.EXCHANGE_TYPE;
    case "adjustment":
      return PortalEventCategory.ADJUSTMENT;
  }
}

// Input schema for getPortalEvents
export const GetPortalEventsInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the events period (YYYY-MM-DD)"),
  endDate: z.string().describe("End date for the events period (YYYY-MM-DD)"),
});

export type GetPortalEventsInput = z.infer<typeof GetPortalEventsInputSchema>;

// Output schema for getPortalEvents
export const GetPortalEventsOutputSchema = z.object({
  events: z
    .array(PortalEventSchema)
    .describe("Array of portal events within the date range"),
});

export type GetPortalEventsOutput = z.infer<typeof GetPortalEventsOutputSchema>;

// RPC definition
export const getPortalEvents = {
  name: "getPortalEvents" as const,
  input: GetPortalEventsInputSchema,
  output: GetPortalEventsOutputSchema,
};
